// Pull-and-release aiming. Drag anywhere; the shell launches opposite to the
// pull. The guide is the real simulation of this shell alone, shown up to its
// first contact plus a short stub after, so "will it reach the rim?" is exact
// but bank shots still need reading.
import * as THREE from 'three';
import { resolveTurn } from '../sim/pool.js';
import { ONE } from '../sim/num.js';
import { CRAB_TINTS } from './water.js';

const MAX_PULL = 4.0;      // world units of drag for full power
const DEAD = 0.07;         // below this fraction a release cancels

// A flat dashed band along a path on the water (world units, so it reads the
// same on every screen density). `along` runs in world units for the dashes.
const bandVS = 'attribute float along; varying float vAlong; void main(){ vAlong = along; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }';
const bandFS = 'uniform vec3 color; uniform float opacity; uniform float dash; uniform float shift; varying float vAlong; void main(){ float on = dash > 0.0 ? step(0.42, fract((vAlong - shift) / dash)) : 1.0; if (on < 0.5) discard; gl_FragColor = vec4(color, opacity); }';
function bandMaterial(color, opacity, dash) {
  return new THREE.ShaderMaterial({ vertexShader: bandVS, fragmentShader: bandFS, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
    uniforms: { color: { value: new THREE.Color(color) }, opacity: { value: opacity }, dash: { value: dash }, shift: { value: 0 } } });
}
function bandGeometry(pts, width, y = 0.06) {
  const pos = [], along = [], idx = [];
  let run = 0;
  for (let k = 0; k < pts.length; k++) {
    const [x, z] = pts[k];
    const [ax, az] = pts[Math.max(0, k - 1)], [bx, bz] = pts[Math.min(pts.length - 1, k + 1)];
    let tx = bx - ax, tz = bz - az; const l = Math.hypot(tx, tz) || 1; tx /= l; tz /= l;
    if (k) run += Math.hypot(x - pts[k - 1][0], z - pts[k - 1][1]);
    pos.push(x - tz * width, y, z + tx * width, x + tz * width, y, z - tx * width);
    along.push(run, run);
    if (k) { const a = (k - 1) * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('along', new THREE.Float32BufferAttribute(along, 1));
  g.setIndex(idx);
  return g;
}

export class Aim {
  constructor(stage, canvas) {
    this.stage = stage;
    this.canvas = canvas;
    this.enabled = false;
    this.group = new THREE.Group();
    this.group.renderOrder = 5;
    stage.scene.add(this.group);
    // Guide: a dashed band to the first contact, a fainter one after it, and
    // an arrowhead where the shell (or what it hits) ends up.
    this.guide = new THREE.Mesh(new THREE.BufferGeometry(), bandMaterial(0xffffff, 0.95, 0.32));
    this.stub = new THREE.Mesh(new THREE.BufferGeometry(), bandMaterial(0xffffff, 0.5, 0.22));
    const head = new THREE.Shape(); head.moveTo(0, 0.26); head.lineTo(-0.17, -0.06); head.lineTo(0.17, -0.06); head.closePath();
    this.arrow = new THREE.Mesh(new THREE.ShapeGeometry(head).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthTest: false }));
    // Thread from the shell to the pull point.
    this.thread = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xefe3c2, transparent: true, opacity: 0.9, depthTest: false }));
    // Power: a thin arc round the shell, drawn up to the pull.
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.78, 0.86, 48, 1, 0, 0.01).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xefe3c2, transparent: true, depthTest: false, side: THREE.DoubleSide }));
    this.ringBack = new THREE.Mesh(new THREE.RingGeometry(0.78, 0.86, 48).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, depthTest: false }));
    // Locked aims of the others.
    this.ghosts = new THREE.Group();
    for (const o of [this.ringBack, this.guide, this.stub, this.arrow, this.thread, this.ring]) { o.renderOrder = 6; o.frustumCulled = false; this.group.add(o); }
    this.group.add(this.ghosts);
    this.plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.ray = new THREE.Raycaster();
    this.drag = null;
    this.hide();
    canvas.addEventListener('pointerdown', e => this.down(e));
    addEventListener('pointermove', e => this.move(e));
    addEventListener('pointerup', e => this.up(e));
    addEventListener('pointercancel', () => this.cancel());
  }

  // Start aiming for crab `id` of `pool` (centre = world [x, z] of the pool).
  begin(pool, centre, id, { onPower, onLock, onCancel, locked }) {
    this.pool = pool; this.centre = centre; this.id = id;
    this.onPower = onPower; this.onLock = onLock; this.onCancel = onCancel;
    this.enabled = true;
    this.drawGhosts(locked);
  }
  end() { this.enabled = false; this.drag = null; this.hide(); this.clearGhosts(); }
  hide() { this.guide.visible = this.stub.visible = this.arrow.visible = this.thread.visible = this.ring.visible = this.ringBack.visible = false; }

  worldAt(e) {
    const r = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(ndc, this.stage.camera);
    const p = new THREE.Vector3();
    return this.ray.ray.intersectPlane(this.plane, p) ? p : null;
  }
  crabWorld() {
    const c = this.pool.crabs[this.id];
    return new THREE.Vector3(this.centre[0] + c.x / ONE, 0, this.centre[1] + c.y / ONE);
  }

  down(e) {
    if (!this.enabled || this.drag) return;
    const p = this.worldAt(e);
    if (!p) return;
    try { this.canvas.setPointerCapture?.(e.pointerId); } catch { /* pointer already gone (e.g. Safari after a system gesture) */ }
    this.drag = { start: p, pointer: e.pointerId, power: 0, detent: false, dx: 0, dz: 0, lastKey: '' };
  }
  move(e) {
    if (!this.drag || e.pointerId !== this.drag.pointer) return;
    const p = this.worldAt(e);
    if (!p) return;
    const d = this.drag;
    d.dx = d.start.x - p.x; d.dz = d.start.z - p.z;      // launch = opposite of pull
    const len = Math.hypot(d.dx, d.dz);
    // Ease the first part so small corrections feel fine, full power is firm.
    const raw = Math.min(1, len / MAX_PULL);
    d.power = raw < 1 ? raw * (0.85 + 0.15 * raw) : 1;
    if (raw >= 1 && !d.detent) { d.detent = true; this.onPower?.(1, true); }
    if (raw < 0.96) d.detent = false;
    this.onPower?.(d.power, false);
    this.draw();
  }
  up(e) {
    if (!this.drag || e.pointerId !== this.drag.pointer) return;
    const d = this.drag;
    this.drag = null;
    this.hide();
    if (d.power < DEAD) { this.onCancel?.(); return; }
    this.onLock?.(this.input(d));
  }
  cancel() { if (this.drag) { this.drag = null; this.hide(); this.onCancel?.(); } }

  input(d) {
    const len = Math.hypot(d.dx, d.dz) || 1;
    return { crab: this.id, dx: Math.round((d.dx / len) * 1000), dy: Math.round((d.dz / len) * 1000), power: Math.round(Math.max(0, Math.min(1, d.power)) * 1000) };
  }

  // Predict this shell alone and split its path at the first contact.
  predict(input) {
    const res = resolveTurn(this.pool, [input], { record: true });
    const first = res.events.find(e => (e.crab === input.crab) && ['rim', 'rock', 'boss', 'clack', 'hug', 'bump'].includes(e.type));
    const at = first ? first.tick : res.frames.length - 1;
    const path = k => res.frames.map(f => [f[k * 2] / ONE + this.centre[0], f[k * 2 + 1] / ONE + this.centre[1]]);
    const pts = path(input.crab);
    // Hitting a friend or a little one: show where *they* go, like a cue line in billiards.
    let follow = input.crab;
    if (first?.type === 'clack') follow = first.crab === input.crab ? first.other : first.crab;
    if (first?.type === 'bump') follow = this.pool.crabs.length + first.little;
    const tail = first ? path(follow).slice(at, Math.min(pts.length, at + (follow === input.crab ? 26 : 40))) : [];
    return { head: pts.slice(0, at + 1), tail, stops: !first };
  }

  draw() {
    const d = this.drag;
    if (!d || d.power < DEAD) { this.hide(); return; }
    const input = this.input(d);
    const key = `${input.dx},${input.dy},${Math.round(input.power / 8)}`;
    const tint = CRAB_TINTS[this.id];
    if (key !== d.lastKey) {
      d.lastKey = key;
      const { head, tail, stops } = this.predict(input);
      const every = (list, step) => list.filter((_, k) => k % step === 0 || k === list.length - 1);
      this.guide.geometry.dispose(); this.stub.geometry.dispose();
      this.guide.geometry = bandGeometry(every(head, 3), 0.055);
      this.stub.geometry = bandGeometry(every(tail, 3), 0.04);
      const ink = tint.clone().lerp(new THREE.Color(1, 1, 1), 0.45);
      this.guide.material.uniforms.color.value.copy(ink);
      this.guide.material.uniforms.opacity.value = stops ? 0.7 : 0.95;
      this.stub.material.uniforms.color.value.copy(ink);
      // Arrowhead at the end of what moves last, pointing along its travel.
      const end = tail.length > 1 ? tail : head;
      const [ex, ez] = end[end.length - 1], [px, pz] = end[Math.max(0, end.length - 4)];
      this.arrow.position.set(ex, 0.07, ez);
      this.arrow.rotation.y = Math.atan2(ex - px, ez - pz) - Math.PI;
      this.arrow.material.color.copy(ink);
      this.arrow.visible = end.length > 1 && Math.hypot(ex - px, ez - pz) > 1e-4;
    }
    const c = this.crabWorld();
    const handle = new THREE.Vector3(c.x - (d.dx / Math.hypot(d.dx, d.dz)) * (0.6 + d.power * 1.9), 0.25, c.z - (d.dz / Math.hypot(d.dx, d.dz)) * (0.6 + d.power * 1.9));
    this.thread.geometry.setFromPoints([new THREE.Vector3(c.x, 0.35, c.z), handle]);
    this.thread.material.opacity = 0.45 + d.power * 0.5;
    // Power arc: grows round the shell; full power turns it the shell's colour.
    const turns = Math.max(0.02, d.power) * Math.PI * 2;
    this.ring.geometry.dispose();
    this.ring.geometry = new THREE.RingGeometry(0.78, 0.86, 48, 1, Math.PI / 2, turns).rotateX(-Math.PI / 2);
    this.ring.position.set(c.x, 0.08, c.z); this.ringBack.position.set(c.x, 0.075, c.z);
    this.ring.material.color.copy(d.power >= 1 ? tint.clone().lerp(new THREE.Color(1, 1, 1), 0.25) : new THREE.Color(0xefe3c2));
    this.guide.visible = this.stub.visible = this.thread.visible = this.ring.visible = this.ringBack.visible = true;
  }

  // Faint dashed lines for aims already locked this turn.
  drawGhosts(locked = []) {
    this.clearGhosts();
    for (const input of locked) {
      const { head } = this.predict(input);
      const geo = bandGeometry(head.filter((_, k) => k % 3 === 0 || k === head.length - 1), 0.035, 0.05);
      const p = new THREE.Mesh(geo, bandMaterial(CRAB_TINTS[input.crab], 0.55, 0.18));
      p.renderOrder = 6; p.frustumCulled = false;
      this.ghosts.add(p);
    }
  }
  clearGhosts() { for (const g of [...this.ghosts.children]) { g.geometry.dispose(); g.material.dispose(); this.ghosts.remove(g); } }

  // Keyboard / test hook: lock a shot directly.
  lockDirect(input) { if (this.enabled) { this.hide(); this.onLock?.(input); } }
}
